import { Button, Container, Typography } from "@mui/material";
import { FastField, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { percentYup } from "../../../common/percentUtils";
import BusinessAutocomplete from "../../../common/components/form/BusinessAutocomplete";
import FormCheckboxWithLabel from "../../../common/components/form/FormCheckboxWithLabel";
import FormColorInput from "../../../common/components/form/FormColorInput";
import { FormTextField } from "../../../common/components/form/FormTextField";
import CollectiveService from "../../../common/service/CollectiveService";
import {
  convertServerToFormValues,
  convertFormToServerValues,
} from "../common/collectiveFormTransformers";

const EditCollective = ({ isNew }) => {
  const { collectiveId } = useParams();
  const navigate = useNavigate();
  const [collective, setCollective] = useState({
    name: "",
    subdomain: "",
    primaryColor: "",
    secondaryColor: "",
    salesTaxPct: 0,
    retailMarkupPct: 0,
    enableRetailSales: false,
    business: null,
    active: false,
  });

  useEffect(() => {
    if (!isNew && collectiveId) {
      const fetchCollective = async () => {
        const data = await CollectiveService.getCollectiveById(collectiveId);
        const business = data.businessId
          ? {
              id: data.businessId,
              name: data.businessName,
              logoPath: data.logoPath,
            }
          : null;
        const formValues = convertServerToFormValues({
          ...data,
          business,
        });
        setCollective(formValues);
      };
      fetchCollective();
    }
  }, [collectiveId, isNew]);

  const handleSubmit = async (values) => {
    const payload = convertFormToServerValues(values);
    if (isNew) {
      await CollectiveService.createCollective(payload);
    } else {
      await CollectiveService.updateCollective(collectiveId, payload);
    }
    navigate("/app/business-admin/manage-collectives");
  };

  return (
    <Container sx={{ pt: 1.5 }}>
      <Typography variant="h2" gutterBottom>
        {isNew ? "Add New Collective" : "Edit Collective"}
      </Typography>
      <Formik
        initialValues={collective}
        validationSchema={Yup.object({
          name: Yup.string().required("Required"),
          subdomain: Yup.string().required("Required"),
          primaryColor: Yup.string().required("Required"),
          secondaryColor: Yup.string().required("Required"),
          enableRetailSales: Yup.boolean(),
          salesTaxPct: percentYup("Sales tax", false).test(
            "salesTaxRequiredForRetail",
            "Sales tax must be greater than 0 when retail sales are enabled",
            function (value) {
              if (this.parent.enableRetailSales) {
                return typeof value === "number" && value > 0;
              }
              return true;
            }
          ),
          retailMarkupPct: percentYup("Retail markup", true, 0, 500).test(
            "retailRequiredWhenEnabled",
            "Retail markup must be greater than 0 when retail sales are enabled",
            function (value) {
              if (this.parent.enableRetailSales) {
                return typeof value === "number" && value > 0;
              }
              return true;
            }
          ),
          business: Yup.object().required("Required"),
        })}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          handleSubmit,
          values,
          setFieldValue,
          setFieldTouched,
          errors,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <FastField
              component={FormTextField}
              name="name"
              label="Collective Name"
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <FastField
              component={FormTextField}
              name="subdomain"
              label="Subdomain"
              fullWidth
              required
              autoCapitalize="off"
              helperText="Subdomain is used in the URL to access your collective. It must be unique across all collectives."
              sx={{ mb: 2 }}
            />
            <FastField
              component={FormColorInput}
              name="primaryColor"
              label="Primary Color"
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <FastField
              component={FormColorInput}
              name="secondaryColor"
              label="Secondary Color"
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <FastField
              component={BusinessAutocomplete}
              name="business"
              label="Business"
              fullWidth
              required
              ignoreCollectiveFilter
              sx={{ mb: 2 }}
            />
            <FastField
              component={FormCheckboxWithLabel}
              name="active"
              label="Active"
              sx={{ mb: 2 }}
            />
            <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
              Taxes and Market Rates
            </Typography>
            <FastField
              component={FormTextField}
              name="salesTaxPct"
              label="Sales Tax (%)"
              type="number"
              fullWidth
              helperText="Enter the percentage rate (e.g., 7.25 for 7.25%). A sales tax rate of zero disables applying sales tax to all transactions."
              inputProps={{ step: "0.001", min: 0, max: 100 }}
              required
              sx={{ mb: 2 }}
            />
            <FastField
              component={FormCheckboxWithLabel}
              name="enableRetailSales"
              label="Enable retail sales"
              onChange={(event) => {
                setFieldValue("enableRetailSales", event.target.checked, true);
                if (!event.target.checked) {
                  setFieldValue("retailMarkupPct", 0, true);
                }
                setFieldTouched("salesTaxPct", true, true);
              }}
              sx={{ mb: 1 }}
            />
            {values.enableRetailSales && (
              <FastField
                component={FormTextField}
                name="retailMarkupPct"
                label="Retail Markup (%)"
                type="number"
                fullWidth
                helperText="Enter the markup percentage to apply to the wholesale price for retail sales (e.g., 10 for 10%). Requires setting a non-zero sales tax percentage."
                inputProps={{ step: "0.001", min: 0, max: 100 }}
                required
                sx={{ mb: 2 }}
              />
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={Object.keys(errors).length > 0 || isSubmitting}
            >
              {isNew ? "Add Collective" : "Save Changes"}
            </Button>
          </form>
        )}
      </Formik>
    </Container>
  );
};

export default EditCollective;
